/*
 * Maddox Gallery — deterministic Featured Collection tabs
 *
 * Why this exists:
 * The Stiletto Featured Collection section already renders the products, but
 * the native tab click path is not reliably applying `selected` / `visible`
 * on the Maddox Curated Art Collections section.
 *
 * This controller owns ONLY the tab-state switch. It leaves Stiletto/Swiper
 * responsible for the carousel itself.
 */
(function () {
  'use strict';

  if (window.__maddoxFeaturedCollectionTabs) return;
  window.__maddoxFeaturedCollectionTabs = true;

  var SECTION_SELECTOR = '[data-section-type="featured-collection-slider"]';
  var TAB_SELECTOR = '[data-navigation-item]';
  var PANEL_SELECTOR = '[data-collection]';
  var NAV_SELECTOR = '[data-navigation]';

  function getSectionFromTarget(target) {
    if (!(target instanceof Element)) return null;

    var tab = target.closest(SECTION_SELECTOR + ' ' + TAB_SELECTOR);
    if (!tab) return null;

    var section = tab.closest(SECTION_SELECTOR);
    if (!section) return null;

    return { section: section, tab: tab };
  }

  function hydrateIfNeeded(section, panel, index) {
    if (!panel || panel.dataset.hydrated === 'true') return false;

    var template = panel.querySelector('template[data-deferred-slider-template]');
    var wrapper = panel.querySelector('.featured-collection-slider__products');

    if (!template || !wrapper) {
      panel.dataset.hydrated = 'true';
      return false;
    }

    wrapper.replaceChildren(document.importNode(template.content, true));
    template.remove();
    panel.dataset.hydrated = 'true';

    section.dispatchEvent(
      new CustomEvent('maddox:featured-collection-hydrated', {
        bubbles: true,
        detail: { index: Number(index) }
      })
    );

    return true;
  }

  function refreshSwiper(panel) {
    /*
     * In this theme the panel itself normally carries the `.swiper` class.
     * The descendant fallback also makes this safe if that markup changes.
     */
    var swiperRoot = panel.matches('.swiper')
      ? panel
      : panel.querySelector('.swiper');

    var swiper = swiperRoot && swiperRoot.swiper;
    if (!swiper) return;

    if (typeof swiper.updateSize === 'function') swiper.updateSize();
    if (typeof swiper.updateSlides === 'function') swiper.updateSlides();
    if (typeof swiper.updateProgress === 'function') swiper.updateProgress();
    if (typeof swiper.updateSlidesClasses === 'function') swiper.updateSlidesClasses();
    if (typeof swiper.update === 'function') swiper.update();

    if (
      swiper.navigation &&
      typeof swiper.navigation.update === 'function'
    ) {
      swiper.navigation.update();
    }
  }

  function activate(section, index) {
    var key = String(index);
    var panel = section.querySelector(
      PANEL_SELECTOR + '[data-collection="' + CSS.escape(key) + '"]'
    );

    if (!panel) return false;

    hydrateIfNeeded(section, panel, key);

    section.querySelectorAll(TAB_SELECTOR).forEach(function (item) {
      var active = item.getAttribute('data-navigation-item') === key;

      item.classList.toggle('selected', active);
      item.setAttribute('aria-selected', active ? 'true' : 'false');
      item.setAttribute('tabindex', active ? '0' : '-1');
    });

    section.querySelectorAll(NAV_SELECTOR).forEach(function (item) {
      var active = item.getAttribute('data-navigation') === key;

      item.classList.toggle('visible', active);
      item.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    section.querySelectorAll(PANEL_SELECTOR).forEach(function (item) {
      var active = item.getAttribute('data-collection') === key;

      /*
       * This section's CSS switches panels with `.visible`.
       * Remove stale Stiletto animation classes so a previous transition cannot
       * override the deterministic state.
       */
      item.classList.toggle('visible', active);
      item.classList.toggle('init-reveal', active);
      item.classList.toggle('reveal', active);
      item.classList.remove('fadeout');

      item.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    /*
     * Hidden Swipers can calculate a zero/incorrect width. Refresh after the
     * newly-active panel has been painted.
     */
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        refreshSwiper(panel);
      });
    });

    section.dispatchEvent(
      new CustomEvent('maddox:featured-collection-tab-change', {
        bubbles: true,
        detail: { index: Number(key) }
      })
    );

    return true;
  }

  /*
   * Capture phase is intentional.
   *
   * The theme currently has a native Stiletto click handler on each tab.
   * We stop this particular click before it reaches that handler so there is
   * exactly ONE owner of the `selected` and `visible` state.
   *
   * This does not affect clicks anywhere else on the page.
   */
  document.addEventListener(
    'click',
    function (event) {
      var match = getSectionFromTarget(event.target);
      if (!match) return;

      var index = match.tab.getAttribute('data-navigation-item');
      if (index === null) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      activate(match.section, index);
    },
    true
  );

  /*
   * Arrow-key navigation for accessibility.
   * Enter/Space already generate a click on the button and use the handler above.
   */
  document.addEventListener('keydown', function (event) {
    if (!(event.target instanceof Element)) return;

    var tab = event.target.closest(SECTION_SELECTOR + ' ' + TAB_SELECTOR);
    if (!tab) return;

    if (
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight' &&
      event.key !== 'Home' &&
      event.key !== 'End'
    ) {
      return;
    }

    var section = tab.closest(SECTION_SELECTOR);
    if (!section) return;

    var tabs = Array.prototype.slice.call(section.querySelectorAll(TAB_SELECTOR));
    var current = tabs.indexOf(tab);
    if (current < 0 || tabs.length < 2) return;

    var next = current;

    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;
    if (event.key === 'ArrowLeft') {
      next = (current - 1 + tabs.length) % tabs.length;
    }
    if (event.key === 'ArrowRight') {
      next = (current + 1) % tabs.length;
    }

    var nextTab = tabs[next];
    var nextIndex = nextTab.getAttribute('data-navigation-item');

    event.preventDefault();
    activate(section, nextIndex);
    nextTab.focus();
  });

  function initialiseSection(section) {
    var tabs = section.querySelectorAll(TAB_SELECTOR);
    var panels = section.querySelectorAll(PANEL_SELECTOR);

    if (!tabs.length || !panels.length) return;

    var selected = section.querySelector(TAB_SELECTOR + '.selected');
    var initialIndex = selected
      ? selected.getAttribute('data-navigation-item')
      : tabs[0].getAttribute('data-navigation-item');

    /*
     * Normalise the server-rendered first state and ARIA attributes without
     * relying on Stiletto's click handler.
     */
    if (initialIndex !== null) {
      activate(section, initialIndex);
    }
  }

  document.querySelectorAll(SECTION_SELECTOR).forEach(initialiseSection);

  /*
   * Shopify Theme Editor can replace section markup without reloading the page.
   * Event delegation already survives that; this only normalises initial state.
   */
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target;
    if (!(root instanceof Element)) return;

    if (root.matches(SECTION_SELECTOR)) {
      initialiseSection(root);
    }

    root.querySelectorAll(SECTION_SELECTOR).forEach(initialiseSection);
  });
})();
